import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { cachedGet, readCache } from "../api";
import { MessageCircle } from "lucide-react";
import BottomNavBar from "../landingPageNew/BottomNavBar";
import HHLogo from "../sidebar/logo.svg";
import { useAmazonContext } from "../../Contexts/AmazonContext";

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 20px 90px 20px;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 15px;
  background-color: #222222;
  border-radius: 10px;
  padding: 10px;
`;

const Title = styled.h1`
  font-size: 24px;
  color: ${(props) => (props.isDarkMode ? "#fff" : "#333")};
  margin: 10px 0 5px 0;
  font-family: "Quicksand", sans-serif;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${(props) => (props.isDarkMode ? "#aaa" : "#666")};
  margin: 0;
  font-family: "Quicksand", sans-serif;
`;

const AnnouncementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AnnouncementCard = styled.div`
  background: ${(props) => (props.isDarkMode ? "#1f1f1f" : "#fff")};
  border: 1px solid ${(props) => (props.isDarkMode ? "#333" : "#e0e0e0")};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  ${(props) =>
    !props.isRead &&
    `
    background: ${props.isDarkMode ? "#2a2440" : "#faf7ff"};
    border-left: 4px solid #8747d4;
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const Icon = styled.div`
  font-size: 32px;
  line-height: 1;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => (props.isDarkMode ? "#fff" : "#333")};
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Quicksand", sans-serif;
`;

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4757;
  flex-shrink: 0;
`;

const CardDate = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${(props) => (props.isDarkMode ? "#aaa" : "#999")};
  font-family: "Quicksand", sans-serif;
`;

const CardPreview = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: ${(props) => (props.isDarkMode ? "#ccc" : "#666")};
  line-height: 1.5;
  font-family: "Quicksand", sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  /* Strip HTML styles from preview */
  * {
    all: unset;
    display: inline;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: ${(props) => (props.isDarkMode ? "#aaa" : "#999")};
  font-family: "Quicksand", sans-serif;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  font-size: 16px;
  color: ${(props) => (props.isDarkMode ? "#aaa" : "#999")};
  font-family: "Quicksand", sans-serif;
`;

const SalaamList = () => {
  const { isDarkMode } = useAmazonContext();
  const history = useHistory();
  const userEmail = localStorage.getItem("userEmail");
  // The nav bar has usually already fetched these, so the list renders with no wait.
  const cachedAnnouncements = readCache(
    "/api/announcements",
    { email: userEmail },
    30000
  );
  const [announcements, setAnnouncements] = useState(
    () => cachedAnnouncements?.data || []
  );
  const [isLoading, setIsLoading] = useState(
    !!userEmail && !cachedAnnouncements
  );

  useEffect(() => {
    if (!userEmail) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    cachedGet("/api/announcements", {
      params: { email: userEmail },
      ttl: 30000,
    })
      .then((data) => {
        if (!cancelled) setAnnouncements(data || []);
      })
      .catch((error) => {
        console.error("Failed to fetch announcements:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userEmail]);

  const handleAnnouncementClick = (announcementId) => {
    history.push(`/salaam/${announcementId}`);
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <>
      <Container>
        <Header>
          <Logo src={HHLogo} alt="HH Courses" />
          <Title isDarkMode={isDarkMode}>Salaam!</Title>
          <Subtitle isDarkMode={isDarkMode}>
            Messages from Here for the Hereafter
          </Subtitle>
        </Header>

        {isLoading ? (
          <LoadingState isDarkMode={isDarkMode}>Loading...</LoadingState>
        ) : announcements.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📭</EmptyIcon>
            <EmptyText isDarkMode={isDarkMode}>Nothing new for you.</EmptyText>
          </EmptyState>
        ) : (
          <AnnouncementsList>
            {announcements.map((announcement) => {
              const isRead = (announcement.readBy || []).includes(userEmail);
              return (
                <AnnouncementCard
                  key={announcement._id}
                  isDarkMode={isDarkMode}
                  isRead={isRead}
                  onClick={() => handleAnnouncementClick(announcement._id)}
                >
                  <CardHeader>
                    <Icon>{announcement.icon || "📢"}</Icon>
                    <CardContent>
                      <CardTitle isDarkMode={isDarkMode}>
                        {announcement.title}
                        {!isRead && <UnreadDot />}
                      </CardTitle>
                      <CardDate isDarkMode={isDarkMode}>
                        {announcement.createdAt
                          ? new Date(announcement.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )
                          : ""}
                      </CardDate>
                    </CardContent>
                  </CardHeader>
                  <CardPreview isDarkMode={isDarkMode}>
                    {stripHtml(announcement.message)}
                  </CardPreview>
                </AnnouncementCard>
              );
            })}
          </AnnouncementsList>
        )}
      </Container>
      <BottomNavBar />
    </>
  );
};

export default SalaamList;

